import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, orderBy, getDoc, getDocs, doc, Timestamp } from 'firebase/firestore';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { showError } from '../utils/notifications';
import { logLogout } from '../services/auditService';

function AuditLogsViewer() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [activeSection, setActiveSection] = useState('audit-logs');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Estados para filtros
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [category, setCategory] = useState('');
  const [action, setAction] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  
  // Listas para filtros
  const [categories, setCategories] = useState([]);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [entities, setEntities] = useState([]);
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const logsPerPage = 20; // 20 logs por página
  const [filteredLogs, setFilteredLogs] = useState([]); // Estado para logs después de filtros
  // eslint-disable-next-line no-unused-vars
  const [totalLogs, setTotalLogs] = useState(0);

  // Formatear fecha para mostrar
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      return String(timestamp);
    }
    
    return date.toLocaleString();
  };

  // Cargar opciones para filtros
  const loadFilterOptions = useCallback(async () => {
    try {
      const logsRef = collection(db, 'auditLogs');
      const querySnapshot = await getDocs(logsRef);
      
      const categoriesSet = new Set();
      const actionsSet = new Set();
      const usersSet = new Set();
      const entitiesSet = new Set();
      
      querySnapshot.forEach(doc => {
        const data = doc.data();
        if (data.categoria) categoriesSet.add(data.categoria);
        if (data.accion) actionsSet.add(data.accion);
        if (data.usuarioRef) {
          const userEmail = data.usuarioRef.split('/')[1];
          if (userEmail) usersSet.add(userEmail);
        }
        if (data.entidad) entitiesSet.add(data.entidad);
      });
      
      setCategories(Array.from(categoriesSet));
      setActions(Array.from(actionsSet));
      setUsers(Array.from(usersSet));
      setEntities(Array.from(entitiesSet));
    } catch (error) {
      console.error("Error al cargar opciones de filtro:", error);
    }
  }, [setCategories, setActions, setUsers, setEntities]);

  // Obtener logs con filtros
  const fetchLogs = useCallback(async (filterOptions = {}) => {
    try {
      setLoading(true);
      console.log("Obteniendo logs de auditoría...");
      
      // Colección de logs
      const logsRef = collection(db, 'auditLogs');
      
      // Construir consulta con filtros
      let queryConstraints = [orderBy('serverTimestamp', 'desc')];
      
      console.log("🔥 AuditLogsViewer: Opciones de filtro recibidas:", filterOptions);

      let startTimestamp, endTimestamp;

      // Aplicar filtro por fecha o establecer el día actual por defecto
      if (filterOptions.startDate && filterOptions.endDate) {
        // Si se especifican fechas, usarlas
        const [startYear, startMonth, startDay] = filterOptions.startDate.split('-').map(Number);
        const startDateLocal = new Date(startYear, startMonth - 1, startDay, 0, 0, 0);
        startTimestamp = Timestamp.fromDate(startDateLocal);

        const [endYear, endMonth, endDay] = filterOptions.endDate.split('-').map(Number);
        const endDateLocal = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
        endTimestamp = Timestamp.fromDate(endDateLocal);

        console.log("🔥 AuditLogsViewer: Filtro Fecha Inicio (Date Local):", startDateLocal);
        console.log("🔥 AuditLogsViewer: Filtro Fecha Inicio (Timestamp):", startTimestamp);
        console.log("🔥 AuditLogsViewer: Filtro Fecha Fin (Date Local):", endDateLocal);
        console.log("🔥 AuditLogsViewer: Filtro Fecha Fin (Timestamp):", endTimestamp);

      } else {
        // Si no se especifican fechas, usar el día actual
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        startTimestamp = Timestamp.fromDate(today);

        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        endTimestamp = Timestamp.fromDate(new Date(tomorrow.getTime() - 1)); // Fin del día actual
        
        // Opcional: establecer los estados de fecha para reflejar el filtro por defecto en la interfaz
        // setStartDate(today.toISOString().split('T')[0]);
        // setEndDate(new Date(tomorrow.getTime() - 1).toISOString().split('T')[0]);

        console.log("🔥 AuditLogsViewer: Cargando logs del día actual.");
        console.log("🔥 AuditLogsViewer: Fecha Inicio (Hoy):", today);
        console.log("🔥 AuditLogsViewer: Fecha Fin (Fin del día):", new Date(tomorrow.getTime() - 1));
      }
      
      queryConstraints.push(where('serverTimestamp', '>=', startTimestamp));
      queryConstraints.push(where('serverTimestamp', '<=', endTimestamp));
      
      // Aplicar otros filtros
      if (filterOptions.category) {
        queryConstraints.push(where('categoria', '==', filterOptions.category));
      }
      
      if (filterOptions.action) {
        queryConstraints.push(where('accion', '==', filterOptions.action));
      }
      
      if (filterOptions.user) { // Assuming user filter is by user email
        queryConstraints.push(where('userId', '==', filterOptions.user));
      }

      if (filterOptions.entity) {
        queryConstraints.push(where('entidad', '==', filterOptions.entity));
      }
      
      const q = query(logsRef, ...queryConstraints);
      const querySnapshot = await getDocs(q);
      
      // Procesar resultados
      const logsList = [];
      querySnapshot.forEach((doc) => {
        const logData = doc.data();
        
        // Extraer usuario del usuarioRef
        const userRef = logData.usuarioRef ? logData.usuarioRef.split('/')[1] || 'N/A' : 'N/A';
        
        logsList.push({
          id: doc.id,
          fecha: formatDate(logData.serverTimestamp),
          categoria: logData.categoria || 'N/A',
          accion: logData.accion || 'N/A',
          usuario: userRef,
          entidad: logData.entidad || 'N/A',
          ip: logData.ip || 'N/A',
          dispositivo: logData.dispositivo || 'N/A',
          detalleJSON: JSON.stringify(logData.detalle || {}, null, 2),
          detalle: logData.detalle || {}
        });
      });
      
      setLogs(logsList);
      setFilteredLogs(logsList);
      setCurrentPage(1);
      setTotalLogs(logsList.length);
      
      console.log(`Logs cargados: ${logsList.length}`);
      
      // Cargar listas para filtros
      await loadFilterOptions();
    } catch (error) {
      console.error("Error al cargar logs de auditoría:", error);
      showError("Error", "No se pudieron cargar los logs de auditoría");
    } finally {
      setLoading(false);
    }
  }, [setLoading, loadFilterOptions, setLogs, setFilteredLogs, setCurrentPage, setTotalLogs]);

  // Manejar filtro
  const handleFilter = (e) => {
    e.preventDefault();
    
    const filterOptions = {};
    if (startDate) filterOptions.startDate = startDate;
    if (endDate) filterOptions.endDate = endDate;
    if (category) filterOptions.category = category;
    if (action) filterOptions.action = action;
    if (userFilter) filterOptions.user = userFilter;
    if (entityFilter) filterOptions.entity = entityFilter;
    
    fetchLogs(filterOptions);
  };

  // Verificación de autenticación y permisos
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const userDocRef = doc(db, 'usuarios', currentUser.email);
          const userDoc = await getDoc(userDocRef);

          if (!userDoc.exists()) {
            showError("Usuario no encontrado", "Tu perfil no existe en el sistema");
            await auth.signOut();
            navigate('/login');
            return;
          }

          const userData = userDoc.data();
          setUser(currentUser);
          setUserRole(userData.rol || 'Usuario');

          // Verificar si el usuario tiene permiso para ver logs
          if (userData.rol !== 'Admin' && userData.rol !== 'Auditor') {
            showError("Acceso denegado", "Solo administradores y auditores pueden ver los logs de auditoría");
            navigate('/dashboard');
            return;
          }

          // Asegurar que loadFilterOptions se carga ANTES de fetchLogs
          await loadFilterOptions();

          // Cargar logs
          fetchLogs();

        } catch (error) {
          console.error("Error al verificar usuario:", error);
          showError("Error", "Ocurrió un problema al verificar tu perfil");
          await auth.signOut();
          navigate('/login');
        }
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate, fetchLogs, loadFilterOptions]);

  // Effect to update filtered logs based on filter states (excluding date, handled in fetchLogs)
  useEffect(() => {
    // This effect is primarily for updates if filter states were bound to inputs directly.
    // Since filters trigger fetchLogs now, this might be less critical.
    // However, if loadFilterOptions updates states, this could react.
    // Re-run fetchLogs when relevant filter states change? Or handle filtering locally here?
    // Given fetchLogs takes filterOptions, it's better to call fetchLogs in handleFilter.
  }, [category, action, userFilter, entityFilter]); // Removed startDate, endDate

  // Calculate logs for the current page
  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = filteredLogs.slice(indexOfFirstLog, indexOfLastLog);
  const totalPages = Math.ceil(filteredLogs.length / logsPerPage);

  // Handle pagination clicks
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Función para interpretar el tipo de acción
  const getActionBadge = (action) => {
    const badgeClasses = {
      insert: "bg-green-100 text-green-800",
      update: "bg-blue-100 text-blue-800",
      delete: "bg-red-100 text-red-800",
      login: "bg-purple-100 text-purple-800",
      login_failed: "bg-red-100 text-red-800",
      logout: "bg-gray-100 text-gray-800",
      confirmacion_entrega: "bg-emerald-100 text-emerald-800"
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeClasses[action] || "bg-gray-100 text-gray-800"}`}>
        {action}
      </span>
    );
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Mobile Header */}
      <MobileHeader 
        title="Logs de Auditoría" 
        userEmail={user?.email}
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      
      {/* Add top spacing for mobile to prevent content from being hidden under header */}
      <div className="md:hidden h-14"></div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Overlay for mobile menu */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}
      
        {/* Sidebar */}
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {/* Top Header (desktop only) */}
          <header className="bg-white shadow px-6 py-4 hidden md:block">
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-bold text-gray-900">Logs de Auditoría</h1>
              <div className="text-right flex items-center">
                <div className="mr-4">
                  <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                  <p className="text-xs text-gray-500">{userRole || "Usuario"}</p>
                </div>
                <button                  onClick={async () => {
                    try {
                      // Registrar cierre de sesión en logs
                      await logLogout({email: user?.email, rol: userRole}, {
                        reason: 'manual',
                        userAgent: navigator.userAgent,
                        platform: navigator.platform,
                        timestamp: new Date().toISOString()
                      });
                      localStorage.removeItem('userRole');
                      await auth.signOut();
                      navigate('/login');
                    } catch (error) {
                      console.error("Error al cerrar sesión:", error);
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1 rounded"
                >
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="p-6">
            {/* Filtros */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">Filtrar Logs</h2>
              <form onSubmit={handleFilter} className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Todas</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Acción</label>
                    <select
                      value={action}
                      onChange={(e) => setAction(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Todas</option>
                      {actions.map(act => (
                        <option key={act} value={act}>{act}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
                    <select
                      value={userFilter}
                      onChange={(e) => setUserFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Todos</option>
                      {users.map(usr => (
                        <option key={usr} value={usr}>{usr}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Entidad</label>
                    <select
                      value={entityFilter}
                      onChange={(e) => setEntityFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Todas</option>
                      {entities.map(ent => (
                        <option key={ent} value={ent}>{ent}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate('');
                      setEndDate('');
                      setCategory('');
                      setAction('');
                      setUserFilter('');
                      setEntityFilter('');
                      fetchLogs();
                    }}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded text-sm"
                  >
                    Limpiar
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm"
                  >
                    Filtrar
                  </button>
                </div>
              </form>
            </div>

            {/* Tabla de Logs */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-700">Registros de Auditoría</h2>
                <p className="text-sm text-gray-600 mt-1">Mostrando {logs.length} de {totalLogs} registros</p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoría</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entidad</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detalles</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.fecha}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.categoria}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{getActionBadge(log.accion)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.usuario}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.entidad}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="max-w-md overflow-hidden text-ellipsis">
                            {log.accion === 'recarga_tickets' ? (
                              <div className="space-y-1">
                                <div className="font-medium">Recarga de Tickets:</div>
                                <div>CA: {log.detalle.caName || 'N/A'}</div>
                                <div>Vehículo: {log.detalle.vehicleType || 'N/A'}</div>
                                <div>Tickets agregados: {log.detalle.ticketsAdded || 0}</div>
                                <div className="text-green-600 font-medium">Precio: ${log.detalle.price?.toFixed(2) || '0.00'}</div>
                                <div>Tickets anteriores: {log.detalle.previousTickets || 0}</div>
                                <div>Nuevos tickets: {log.detalle.newTickets || 0}</div>
                              </div>
                            ) : log.accion === 'recarga_balance' ? (
                              <div className="space-y-1">
                                <div className="font-medium">Recarga de Balance:</div>
                                <div>CA: {log.detalle.caName || 'N/A'}</div>
                                <div className="text-green-600 font-medium">Monto: ${log.detalle.amount?.toFixed(2) || '0.00'}</div>
                                <div>Balance anterior: ${log.detalle.previousBalance?.toFixed(2) || '0.00'}</div>
                                <div>Nuevo balance: ${log.detalle.newBalance?.toFixed(2) || '0.00'}</div>
                              </div>
                            ) : log.accion === 'Descarga de Reporte' ? (
                              <div className="space-y-1">
                                <div className="font-medium">Descarga de Reporte:</div>
                                <div>Archivo: {log.detalle.nombreArchivo || 'N/A'}</div>
                                <div>Registros: {log.detalle.cantidadRegistros || 0}</div>
                                {log.detalle.fechaInicioFiltro && <div>Fecha Inicio: {log.detalle.fechaInicioFiltro}</div>}
                                {log.detalle.fechaFinFiltro && <div>Fecha Fin: {log.detalle.fechaFinFiltro}</div>}
                              </div>
                            ) : log.accion === 'confirmacion_entrega' ? (
                              <div className="space-y-1">
                                <div className="font-medium text-green-600">💰 Confirmación de Entrega:</div>
                                <div className="font-semibold text-green-700">Monto: ${log.detalle.amount?.toFixed(2) || '0.00'}</div>
                                <div>ID Recarga: {log.detalle.rechargeId || 'N/A'}</div>
                                <div>Ejecutor: {log.detalle.executorName || 'N/A'}</div>
                                <div>Página: {log.detalle.confirmedFromPage || 'N/A'}</div>
                                {log.detalle.consolidatedEntry && <div className="text-blue-600">📋 Entrada consolidada</div>}
                                <div className="text-xs text-gray-500">Confirmado: {log.fecha}</div>
                              </div>
                            ) : log.detalle.mensaje || (log.detalle.monto ? 
                              `Monto: ${log.detalle.monto.antes || 0} → ${log.detalle.monto.despues || 0}` : 
                              'Sin detalles')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.ip}</td>
                      </tr>
                    ))}
                    {currentLogs.length === 0 && filteredLogs.length > 0 && (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                          No se encontraron registros en esta página.
                        </td>
                      </tr>
                    )}
                     {filteredLogs.length === 0 && (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                          No se encontraron registros de auditoría que coincidan con los filtros.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Paginación */}
              {filteredLogs.length > logsPerPage && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Siguiente
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Mostrando <span className="font-medium">{indexOfFirstLog + 1}</span> a{' '}
                        <span className="font-medium">
                          {Math.min(indexOfLastLog, filteredLogs.length)}
                        </span>{' '}
                        de <span className="font-medium">{filteredLogs.length}</span> registros
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={handlePrevPage}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                        >
                          <span className="sr-only">Anterior</span>
                          &larr;
                        </button>
                        {/* Page Numbers (simplified) */}
                        {Array.from({ length: totalPages }).map((_, idx) => (
                          <button
                            key={idx + 1}
                            onClick={() => handlePageChange(idx + 1)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === idx + 1 ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                          >
                            {idx + 1}
                          </button>
                        ))}
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                        >
                          <span className="sr-only">Siguiente</span>
                          &rarr;
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default AuditLogsViewer;
